<template lang="html">
  <div v-if="hasContent" class="promotion-status-list flex flex-col gap-2 text-sm">
    <div v-if="applied.length" class="flex flex-col gap-1.5">
      <div class="text-xs font-medium tracking-wide text-muted uppercase">
        Применено
      </div>

      <div
        v-for="(promotion, index) in applied"
        :key="`applied-${promotion.promotionId ?? index}`"
        class="flex items-center gap-2"
      >
        <UIcon name="i-lucide-check" class="size-4 shrink-0 text-success" />
        <span class="min-w-0 flex-1 truncate">{{ promotion.publicTitle || 'Акция' }}</span>
        <span
          class="shrink-0 font-medium tabular-nums"
          :class="benefitInfo(promotion).class"
        >
          {{ benefitInfo(promotion).text }}
        </span>
        <PromoTermsPopover :terms="getTerms(promotion)" />
      </div>
    </div>

    <UCollapsible v-if="rejected.length">
      <UButton
        variant="link"
        color="neutral"
        size="xs"
        class="w-fit p-0"
        trailing-icon="i-lucide-chevron-down"
      >
        Не применено ({{ rejected.length }})
      </UButton>

      <template #content>
        <div class="mt-1.5 flex flex-col gap-1.5">
          <div
            v-for="(promotion, index) in rejected"
            :key="`rejected-${promotion.promotionId ?? index}`"
            class="flex items-center gap-2 text-muted"
          >
            <UIcon name="i-lucide-circle-alert" class="size-4 shrink-0" />
            <span class="min-w-0 flex-1 truncate">{{ promotion.publicTitle || 'Акция не применена' }}</span>
            <PromoTermsPopover :terms="getTerms(promotion)" />
          </div>
        </div>
      </template>
    </UCollapsible>

    <UAlert
      v-if="showPromoRejectedHint"
      color="warning"
      variant="soft"
      icon="i-lucide-ticket"
      title="Промокод не сработал"
      description="Проверьте условия акции ниже или измените состав заказа."
    />
  </div>
</template>

<script setup lang="ts">
import type {
  CartAppliedPromotionDto,
  CartRejectedPromotionDto,
  OrderPromotionDto,
} from '@repo/api/web/models'
import { PromotionRewardType } from '@repo/api/web/models'

import PromoTermsPopover from './PromoTermsPopover.vue'

type AppliedPromotion = CartAppliedPromotionDto | OrderPromotionDto
type RejectedPromotion = CartRejectedPromotionDto

const props = withDefaults(
  defineProps<{
    applied?: AppliedPromotion[]
    rejected?: RejectedPromotion[]
    activePromoCode?: string | null
  }>(),
  {
    applied: () => [],
    rejected: () => [],
  },
)

const { $f } = useFormatter()

const hasContent = computed(
  () =>
    props.applied.length > 0
    || props.rejected.length > 0
    || showPromoRejectedHint.value,
)

const showPromoRejectedHint = computed(
  () =>
    Boolean(props.activePromoCode)
    && !props.applied.length
    && props.rejected.length > 0,
)

function getRewardType(promotion: AppliedPromotion) {
  return 'rewardType' in promotion
    ? promotion.rewardType
    : promotion.rewardTypeSnapshot
}

function rewardTypeLabel(rewardType?: PromotionRewardType | null): string {
  switch (rewardType) {
    case PromotionRewardType.GIFT:
      return 'Подарок'
    case PromotionRewardType.BUNDLE:
      return 'Комплект'
    case PromotionRewardType.NPLUSM:
      return 'N+M'
    case PromotionRewardType.FREESHIPPING:
      return 'Доставка'
    case PromotionRewardType.FIXEDPRICE:
      return 'Фикс. цена'
    case PromotionRewardType.PERCENTDISCOUNT:
    case PromotionRewardType.FIXEDAMOUNTDISCOUNT:
      return 'Скидка'
    case PromotionRewardType.PERCENTCASHBACK:
    case PromotionRewardType.FIXEDAMOUNTCASHBACK:
      return 'Кэшбэк'
    case PromotionRewardType.POINTSMULTIPLIER:
      return 'Баллы'
    default:
      return 'Акция'
  }
}

function benefitInfo(promotion: AppliedPromotion): { text: string, class: string } {
  if (promotion.discountAmount) {
    return { text: `−${$f.currency(promotion.discountAmount)}`, class: 'text-success' }
  }

  if (promotion.cashbackAmount) {
    return { text: `+${$f.currency(promotion.cashbackAmount)} кэшбэк`, class: 'text-info' }
  }

  if (promotion.pointsMultiplier) {
    return { text: `×${promotion.pointsMultiplier} баллов`, class: 'text-info' }
  }

  return { text: rewardTypeLabel(getRewardType(promotion)), class: 'text-muted' }
}

function getTerms(
  promotion: AppliedPromotion | RejectedPromotion,
): string | null {
  if ('terms' in promotion && promotion.terms) {
    return promotion.terms
  }

  if ('termsSnapshot' in promotion && promotion.termsSnapshot) {
    return promotion.termsSnapshot
  }

  return null
}
</script>
